import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, BookOpen, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.articles || d?.results || []);
const empty = { title: "", category: "", summary: "", content: "", image_url: "" };

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setArticles(asList(await api.education())); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ title: a.title || a.name || "", category: a.category || "", summary: a.summary || a.excerpt || "", content: a.content || a.body || "", image_url: a.image_url || a.cover || "" }); setEditId(a.id || a._id); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      const summary = form.summary?.trim() || (form.content || "").slice(0, 200) || form.title;
      const body = { title: form.title, category: form.category || undefined, summary, content: form.content || form.summary || form.title, image_url: form.image_url || undefined };
      if (editId) await api.updateEducation(editId, body); else await api.createEducation(body);
      toast.success(editId ? "Articolo aggiornato" : "Articolo creato");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.deleteEducation(id); toast.success("Articolo eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Articoli educativi"
        subtitle="Crea e gestisci i contenuti per i pazienti."
        action={<Button onClick={openNew} data-testid="create-article-button"><Plus className="mr-2 h-4 w-4" /> Nuovo articolo</Button>}
      />

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        articles.length === 0 ? <EmptyState title="Nessun articolo" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="articles-list">
            {articles.map((a, i) => {
              const id = a.id || a._id;
              return (
                <Card key={id || i} className="flex flex-col border-border/70">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent/20 text-accent-foreground"><BookOpen className="h-4 w-4" /></div>
                      <CardTitle className="text-base leading-snug">{a.title || a.name}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col">
                    {a.category && <Badge variant="secondary" className="mb-2 w-fit">{a.category}</Badge>}
                    <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{a.summary || a.content || a.body}</p>
                    <div className="mt-3 flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`edit-article-${i}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Modifica</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-article-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Eliminare l'articolo?</AlertDialogTitle><AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => remove(id)}>Elimina</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="article-title-input" /></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" /></div>
            <div><Label>URL immagine</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Riassunto</Label><Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Contenuto</Label><Textarea rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1.5" data-testid="article-content-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-article-button">{saving ? "Salvataggio..." : "Salva"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
